/**
 * StatusBar — top status panel for the Play route.
 *
 * Visual concept: a strip of cream campaign-journal paper torn from a
 * pad and pinned to the slate map. Hand-stamped chips, your colour
 * shown as a tape strip, the current actor stamped at a slight tilt,
 * and a circular Help button.
 *
 *   ╔══════════════════════════════════════════════════════════════════╗
 *   ║  ROUND·TURN                                                      ║
 *   ║                              [▮ YOU · Alice]  →  [● Bob to act] ?║
 *   ╚════════════════════════ wax-red dashed pencil margin ═══════════╝
 *
 * The phase chip was originally removed when the simplified-play
 * autopilot took over the non-combat phases — the chip flashed
 * meaningless labels for ~150ms between turns. It returns now in a
 * smaller, friendlier form because user-testing revealed first-time
 * players didn't realise there were distinct attack and defense phases.
 * The chip is paired with a `?` info button that reveals a one-line
 * explanation of the current phase on hover/focus, giving the player a
 * permanent reference for "what does this phase do?". PhaseCoach
 * beneath the bar still carries the plain-English "what to do now"
 * action instruction.
 *
 * Pure projection of GameState — never dispatches reducer actions, but
 * does emit a window event (`qd:show-howto`) when the Help button is
 * pressed so HowToPlayModal can re-open without prop drilling.
 */

import { type ReactNode, useCallback, useState } from 'react';
import type { Player, PlayerColorKey, TurnPhase } from '@qd/protocol';
import { fontStacks, playerColors, surfaces } from '@qd/design-tokens';
import { HOW_TO_PLAY_EVENT } from './HowToPlayModal';
import { useTurnPhase } from '../../stores/gameStore';
import { PHASE_EXPLANATIONS } from './PhaseTransitionToast';
import { formatTimerMMSS, useTurnTimer } from '../../hooks/useTurnTimer';
import {
  DocumentDeployIcon,
  DraftPlanIcon,
  ExecuteTestsIcon,
  IncidentFortifyIcon,
} from './icons';

interface StatusBarProps {
  readonly roundNumber: number;
  readonly turnNumber: number;
  /** GameState.currentPlayerIdx — used together with turnNumber to detect turn changes. */
  readonly currentPlayerIdx: number;
  readonly currentPlayer: Player | null;
  readonly selfPlayer: Player | null;
  readonly isMyTurn: boolean;
  readonly sessionId: string;
  /** Per-turn time limit from session settings, in seconds. 0 = disabled. */
  readonly turnTimerSeconds: number;
}

// -----------------------------------------------------------------------------
// StatusBar
// -----------------------------------------------------------------------------

export function StatusBar({
  roundNumber,
  turnNumber,
  currentPlayerIdx,
  currentPlayer,
  selfPlayer,
  isMyTurn,
  turnTimerSeconds,
}: StatusBarProps): ReactNode {
  const showArrow =
    !!selfPlayer && !!currentPlayer && currentPlayer.id !== selfPlayer.id;

  return (
    <div
      role="status"
      aria-label="Match status"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 14,
        flexWrap: 'nowrap',
        // Deep navy matching the ocean on the map, with a red accent line
        background: 'linear-gradient(90deg, #0c1445 0%, #0a1628 50%, #0c1445 100%)',
        borderBottom: `2px solid #ef4444`,
        boxShadow: '0 2px 20px rgba(0,0,0,0.5)',
        color: '#f8fafc',
        fontFamily: fontStacks.body,
        fontSize: 13,
        padding: '0 12px',
        height: 56,
      }}
    >
      {/*
        StatusBar is a desktop-only surface (Play.tsx routes mobile to
        MobilePlayLayout, which uses MobileStatusBar instead). The flex
        sections used to declare `minWidth: 140 / 160 / 180` (480px total),
        which forced horizontal overflow on tablet-narrow desktops
        (768–1024px range) and on a desktop browser dragged narrow. We
        clamp each section's minWidth to a viewport-relative cap so the
        bar reflows gracefully at sub-1024px widths while retaining the
        intended balanced layout on a normal-width desktop.
      */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 'min(140px, 30vw)' }}>
        <RoundChip roundNumber={roundNumber} turnNumber={turnNumber} />
        <PhaseChip />
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', flex: 1, minWidth: 'min(160px, 32vw)' }}>
        <YouChip selfPlayer={selfPlayer} isMyTurn={isMyTurn} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 14, flex: 1, minWidth: 'min(180px, 36vw)' }}>
        <TimerChip
          turnTimerSeconds={turnTimerSeconds}
          turnNumber={turnNumber}
          currentPlayerIdx={currentPlayerIdx}
        />
        {showArrow ? <ArrowGlyph /> : null}
        <CurrentPlayerChip currentPlayer={currentPlayer} hideIfSelf={selfPlayer} />
        <HowToPlayButton />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// RoundChip — small mono pencil note with a mustard underline
// -----------------------------------------------------------------------------

function RoundChip({
  roundNumber,
  turnNumber,
}: {
  roundNumber: number;
  turnNumber: number;
}): ReactNode {
  return (
    <span
      style={{
        position: 'relative',
        fontFamily: fontStacks.mono,
        fontSize: 11,
        letterSpacing: '0.16em',
        color: '#94a3b8',
        opacity: 0.92,
        paddingBottom: 4,
      }}
    >
      ROUND {roundNumber} · TURN {turnNumber}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          right: 12,
          bottom: 0,
          height: 2,
          background: '#eab308',
          opacity: 0.78,
          borderRadius: 1,
        }}
      />
    </span>
  );
}

// -----------------------------------------------------------------------------
// PhaseChip — compact "you are here in the turn loop" indicator with an
// inline `?` info button. The chip itself is decorative; the `?` opens a
// small absolutely-positioned tooltip explaining what the current phase
// actually does. The tooltip is opened on hover (mouse) or focus
// (keyboard / tap-to-focus on touch) and closed when both leave.
// -----------------------------------------------------------------------------

const PHASE_LABELS: Readonly<Record<TurnPhase, string>> = {
  'draft-plan': 'REINFORCING',
  'document-deploy': 'DEPLOYING DOCS',
  'execute-tests': 'ATTACK',
  'incident-fortify': 'DEFENSE',
};

const PHASE_TINTS: Readonly<Record<TurnPhase, string>> = {
  'draft-plan': playerColors.mustard.hex,
  'document-deploy': playerColors.prussian.hex,
  'execute-tests': surfaces.waxRed,
  'incident-fortify': playerColors.verdigris.hex,
};

function PhaseGlyph({
  phase,
  color,
}: {
  phase: TurnPhase;
  color: string;
}): ReactNode {
  switch (phase) {
    case 'draft-plan':
      return <DraftPlanIcon size={14} color={color} />;
    case 'document-deploy':
      return <DocumentDeployIcon size={14} color={color} />;
    case 'execute-tests':
      return <ExecuteTestsIcon size={14} color={color} />;
    case 'incident-fortify':
      return <IncidentFortifyIcon size={14} color={color} />;
  }
}

function PhaseChip(): ReactNode {
  const phase = useTurnPhase();
  if (phase === null) return null;

  const tint = PHASE_TINTS[phase];
  const label = PHASE_LABELS[phase];
  const explanation = PHASE_EXPLANATIONS[phase];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 8px',
        border: `1px solid ${hexAlpha(tint, 0.45)}`,
        borderLeft: `3px solid ${tint}`,
        borderRadius: 3,
        background: hexAlpha(tint, 0.12),
        fontFamily: fontStacks.mono,
        fontSize: 10,
        letterSpacing: '0.16em',
        color: '#e2e8f0',
        fontWeight: 700,
      }}
    >
      <PhaseGlyph phase={phase} color={tint} />
      <span>{label}</span>
      <PhaseInfoButton explanation={explanation} tint={tint} />
    </span>
  );
}

function PhaseInfoButton({
  explanation,
  tint,
}: {
  explanation: string;
  tint: string;
}): ReactNode {
  // Hover and focus are tracked separately so a keyboard user can Tab to
  // the `?`, see the tooltip, then Tab away — and a mouse user gets the
  // same behaviour on hover. On touch the button is focusable, so a single
  // tap opens it; tapping elsewhere blurs it shut.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const open = hovered || focused;

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
    >
      <button
        type="button"
        aria-label={`What is this phase? ${explanation}`}
        aria-describedby={open ? 'qd-phase-tooltip' : undefined}
        aria-expanded={open}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          marginLeft: 2,
          width: 14,
          height: 14,
          borderRadius: '50%',
          border: `1px solid ${hexAlpha('#94a3b8', 0.55)}`,
          background: hexAlpha('#1e293b', 0.8),
          color: '#94a3b8',
          fontFamily: fontStacks.body,
          fontSize: 9,
          fontWeight: 700,
          lineHeight: 1,
          cursor: 'help',
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ?
      </button>
      {open ? (
        <span
          id="qd-phase-tooltip"
          role="tooltip"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: -6,
            zIndex: 9,
            minWidth: 220,
            maxWidth: 'min(72vw, 320px)',
            padding: '8px 10px',
            background: 'rgba(10, 17, 35, 0.97)',
            border: `1px solid ${hexAlpha('#94a3b8', 0.3)}`,
            borderLeft: `3px solid ${tint}`,
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            fontFamily: fontStacks.body,
            fontSize: 12,
            letterSpacing: 0,
            textTransform: 'none',
            fontWeight: 400,
            color: '#cbd5e1',
            lineHeight: 1.35,
            pointerEvents: 'none',
          }}
        >
          {explanation}
        </span>
      ) : null}
    </span>
  );
}

// -----------------------------------------------------------------------------
// YouChip — persistent "this is you" anchor with a tape strip in your colour
// -----------------------------------------------------------------------------

function YouChip({
  selfPlayer,
  isMyTurn,
}: {
  selfPlayer: Player | null;
  isMyTurn: boolean;
}): ReactNode {
  if (!selfPlayer) return null;
  const swatch = colorHex(selfPlayer.color);
  return (
    <span
      title={`You are ${selfPlayer.name} (${selfPlayer.color})`}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        paddingLeft: 14,
        paddingRight: 12,
        paddingTop: 4,
        paddingBottom: 4,
        border: `1.2px ${isMyTurn ? 'solid' : 'dashed'} ${
          isMyTurn ? swatch : 'rgba(148,163,184,0.35)'
        }`,
        borderRadius: 3,
        background: isMyTurn
          ? hexAlpha(swatch, 0.2)
          : 'rgba(30, 41, 59, 0.6)',
        boxShadow: isMyTurn
          ? `0 0 14px ${hexAlpha(swatch, 0.45)}`
          : undefined,
        color: '#e2e8f0',
        fontFamily: fontStacks.mono,
        fontSize: 10,
        letterSpacing: '0.14em',
        transition: 'background-color 200ms ease, box-shadow 200ms ease',
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 5,
          background: swatch,
          borderTopLeftRadius: 3,
          borderBottomLeftRadius: 3,
        }}
      />
      <span style={{ fontWeight: 700, color: '#f1f5f9' }}>YOU</span>
      <span style={{ opacity: 0.45, color: '#94a3b8' }}>·</span>
      <span
        style={{
          fontFamily: fontStacks.body,
          fontSize: 12,
          letterSpacing: 0,
          color: '#f1f5f9',
          fontWeight: 600,
        }}
      >
        {selfPlayer.name}
      </span>
      {isMyTurn ? (
        <span
          style={{
            color: swatch,
            fontWeight: 800,
            marginLeft: 4,
            fontSize: 10,
            letterSpacing: '0.14em',
            borderBottom: `2px solid ${swatch}`,
          }}
        >
          ★ YOUR TURN
        </span>
      ) : null}
    </span>
  );
}

// -----------------------------------------------------------------------------
// TimerChip — per-turn countdown. Hidden when timer is disabled (0 seconds).
// Pulses wax-red and tints its background once the remaining time drops
// below 10 seconds.
// -----------------------------------------------------------------------------

function TimerChip({
  turnTimerSeconds,
  turnNumber,
  currentPlayerIdx,
}: {
  turnTimerSeconds: number;
  turnNumber: number;
  currentPlayerIdx: number;
}): ReactNode {
  const { enabled, remainingSec } = useTurnTimer({
    durationSec: turnTimerSeconds,
    turnNumber,
    currentPlayerIdx,
  });
  if (!enabled) return null;

  const urgent = remainingSec <= 10;
  const expired = remainingSec === 0;
  const accent = urgent ? '#f87171' : '#60a5fa';

  return (
    <span
      role="timer"
      aria-live="off"
      aria-label={`Turn timer: ${remainingSec} seconds remaining`}
      title="Time remaining for the current player's turn"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        border: `1.4px ${urgent ? 'solid' : 'dashed'} ${accent}`,
        borderRadius: 3,
        background: urgent
          ? hexAlpha('#ef4444', expired ? 0.28 : 0.16)
          : 'rgba(30, 41, 59, 0.6)',
        boxShadow: urgent
          ? `0 0 12px ${hexAlpha('#ef4444', expired ? 0.7 : 0.45)}`
          : undefined,
        color: accent,
        fontFamily: fontStacks.mono,
        animation: urgent ? 'qd-timer-pulse 1s ease-in-out infinite' : undefined,
      }}
    >
      <TimerHourglass urgent={urgent} accent={accent} />
      <span
        style={{
          fontSize: 14,
          letterSpacing: '0.08em',
          color: accent,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatTimerMMSS(remainingSec)}
      </span>
      {/* One-shot keyframes injection — safe to repeat across instances. */}
      <style>{`@keyframes qd-timer-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.04); } }`}</style>
    </span>
  );
}

function TimerHourglass({ urgent, accent }: { urgent: boolean; accent: string }): ReactNode {
  return (
    <svg
      aria-hidden
      width={12}
      height={14}
      viewBox="0 0 12 14"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <path
        d="M2 1 H10 M2 13 H10 M2 1 L6 6 L10 1 M2 13 L6 8 L10 13 Z"
        stroke={accent}
        strokeWidth={1.2}
        fill={urgent ? hexAlpha(accent, 0.3) : 'none'}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// -----------------------------------------------------------------------------
// ArrowGlyph — small "→" rendered between you and the current actor
// -----------------------------------------------------------------------------

function ArrowGlyph(): ReactNode {
  return (
    <span
      aria-hidden
      style={{
        fontFamily: fontStacks.mono,
        fontSize: 14,
        color: surfaces.ink,
        opacity: 0.55,
        userSelect: 'none',
      }}
    >
      →
    </span>
  );
}

// -----------------------------------------------------------------------------
// CurrentPlayerChip — slightly tilted dashed "stamp" tag
// -----------------------------------------------------------------------------

function CurrentPlayerChip({
  currentPlayer,
  hideIfSelf,
}: {
  currentPlayer: Player | null;
  hideIfSelf: Player | null;
}): ReactNode {
  if (!currentPlayer) {
    return (
      <span
        style={{
          fontFamily: fontStacks.mono,
          fontSize: 10,
          letterSpacing: '0.14em',
          color: '#94a3b8',
          opacity: 0.7,
        }}
      >
        WAITING…
      </span>
    );
  }
  if (hideIfSelf && currentPlayer.id === hideIfSelf.id) return null;

  const swatch = colorHex(currentPlayer.color);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 10px',
        border: `1.2px dashed rgba(148,163,184,0.4)`,
        borderRadius: 3,
        background: hexAlpha(swatch, 0.12),
        transform: 'rotate(-1.2deg)',
        fontFamily: fontStacks.body,
        fontSize: 12,
        color: '#e2e8f0',
        boxShadow: `0 2px 8px ${hexAlpha(swatch, 0.2)}`,
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 11,
          height: 11,
          borderRadius: 2,
          background: swatch,
          border: `1px solid rgba(255,255,255,0.2)`,
          opacity: 0.95,
        }}
      />
      <span style={{ fontWeight: 700, color: '#f1f5f9' }}>{currentPlayer.name}</span>
      <span
        style={{
          fontFamily: fontStacks.mono,
          fontSize: 9,
          letterSpacing: '0.16em',
          opacity: 0.55,
          color: '#94a3b8',
          marginLeft: 2,
        }}
      >
        TO ACT
      </span>
    </span>
  );
}

// -----------------------------------------------------------------------------
// HowToPlayButton — circular "?" that re-opens the HowToPlayModal
// -----------------------------------------------------------------------------

function HowToPlayButton(): ReactNode {
  const handleClick = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(HOW_TO_PLAY_EVENT));
  }, []);

  return (
    <button
      type="button"
      onClick={handleClick}
      title="How to play"
      aria-label="Show how-to-play tutorial"
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        border: `1.4px solid rgba(148,163,184,0.4)`,
        background: 'rgba(234, 179, 8, 0.25)',
        color: '#fde68a',
        fontFamily: fontStacks.display,
        fontSize: 14,
        fontWeight: 800,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        boxShadow: `0 2px 4px rgba(0,0,0,0.3), inset 0 -2px 0 rgba(234,179,8,0.3)`,
        transition: 'transform 120ms ease, background-color 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(234, 179, 8, 0.45)';
        e.currentTarget.style.transform = 'translateY(-1px) scale(1.05)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(234, 179, 8, 0.25)';
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
      }}
    >
      ?
    </button>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function colorHex(color: PlayerColorKey): string {
  return playerColors[color].hex;
}

function hexAlpha(hex: string, alpha: number): string {
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return hex;
  const r = parseInt(m[1]!, 16);
  const g = parseInt(m[2]!, 16);
  const b = parseInt(m[3]!, 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
